import { useAnalytics } from '@/hooks/useAnalytics';
import type { SectionProps } from '@/types';

export function MapSection({ business, config }: SectionProps) {
  const title = (config.title as string) || 'Find us';
  const { track } = useAnalytics(business.id);

  if (!business.latitude || !business.longitude) {
    if (!business.address_line) return null;
  }

  const locationQuery =
    business.address_line
      ? `${business.address_line}, ${business.city?.name ?? ''}, ${business.state?.name ?? ''}`.trim()
      : business.latitude && business.longitude
        ? `${business.latitude},${business.longitude}`
        : '';

  const encodedQuery = encodeURIComponent(locationQuery);

  // No Google Maps API key needed — standard iframe embed (same as static sites).
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
  const embedUrl =
    business.latitude && business.longitude
      ? `https://maps.google.com/maps?q=${business.latitude},${business.longitude}&z=15&output=embed`
      : `https://maps.google.com/maps?q=${encodedQuery}&z=15&output=embed`;

  const handleOpenMap = () => {
    track('map_opened');
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="section-card overflow-hidden p-0">
      <div className="p-6 pb-0">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {business.address_line && (
          <p className="mt-2 text-sm text-slate-600">{business.address_line}</p>
        )}
      </div>

      <div className="relative mt-4 aspect-[16/9] w-full bg-slate-100">
        <iframe
          title={`Map showing location of ${business.name}`}
          src={embedUrl}
          className="h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>

      <div className="flex flex-wrap gap-3 p-6">
        <button
          type="button"
          onClick={handleOpenMap}
          className="inline-flex items-center gap-2 rounded-brand bg-brand-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Open in Google Maps
        </button>
        {business.phone && (
          <a
            href={`tel:${business.phone}`}
            onClick={() => track('call_clicked')}
            className="inline-flex items-center gap-2 rounded-brand border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Call for directions
          </a>
        )}
      </div>
    </section>
  );
}
